import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import GlobalSearch from "@/components/GlobalSearch";
import TrialBanner from "@/components/TrialBanner";
import AriWidget from "@/components/AriWidget";
import NormaGate from "@/components/NormaGate";
import DiagnosticoRapido from "@/pages/DiagnosticoRapido";
import {
  LayoutDashboard, FileText, Map, AlertTriangle, Bell, FileBarChart,
  Building2, Users, Radio, LogOut, ShieldCheck, ClipboardList, ClipboardCheck, Eye, Settings, Cog, GitBranch, Boxes, Factory, Send, Network, Tag, Menu, X, Lock,
} from "lucide-react";

// `nivel`: nivel de plan al que pertenece el módulo (null = disponible en todos los planes).
// N1 Documenta · N2 Registra · N3 Traza · N4 Verifica.
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["superadmin", "admin", "calidad", "operario", "consulta"] },
  { to: "/documentos", label: "Documentos", icon: FileText, roles: ["superadmin", "admin", "calidad", "consulta"], nivel: 1 },
  { to: "/norma", label: "Mapa Normativo", icon: Map, roles: ["superadmin", "admin", "calidad", "consulta"], nivel: 1 },
  { to: "/calidad", label: "Calidad", icon: ClipboardCheck, roles: ["superadmin", "admin", "calidad", "operario"], nivel: 2 },
  { to: "/incidencias", label: "Incidencias", icon: ClipboardList, roles: ["superadmin", "admin", "calidad", "operario", "consulta"], nivel: 2 },
  { to: "/maestros", label: "Maestros", icon: GitBranch, roles: ["superadmin", "admin", "calidad", "operario", "consulta"], nivel: 3 },
  { to: "/lotes", label: "Recepciones y Lotes", icon: Boxes, roles: ["superadmin", "admin", "calidad", "operario", "consulta"], nivel: 3 },
  { to: "/produccion", label: "Producción", icon: Factory, roles: ["superadmin", "admin", "calidad", "operario", "consulta"], nivel: 3 },
  { to: "/expediciones", label: "Expediciones", icon: Send, roles: ["superadmin", "admin", "calidad", "operario", "consulta"], nivel: 3 },
  { to: "/traza", label: "Trazabilidad", icon: Network, roles: ["superadmin", "admin", "calidad", "operario", "consulta"], nivel: 3 },
  { to: "/etiquetas", label: "Etiquetas", icon: Tag, roles: ["superadmin", "admin", "calidad", "operario"], nivel: 3 },
  { to: "/gaps", label: "No conformidades", icon: AlertTriangle, roles: ["superadmin", "admin", "calidad", "consulta"], nivel: 4 },
  { to: "/alertas", label: "Alertas", icon: Bell, roles: ["superadmin", "admin", "calidad", "operario", "consulta"], nivel: 4 },
  { to: "/informes", label: "Informes", icon: FileBarChart, roles: ["superadmin", "admin", "calidad", "consulta"], nivel: 4 },
  { to: "/inspector", label: "Modo Inspector", icon: Eye, roles: ["superadmin", "admin", "calidad", "consulta"], nivel: 4 },
  { to: "/ajustes", label: "Ajustes", icon: Settings, roles: ["superadmin", "admin", "calidad", "operario", "consulta"] },
  { to: "/usuarios", label: "Usuarios", icon: Users, roles: ["superadmin", "admin"] },
  { to: "/nucleo", label: "Eventos Núcleo", icon: Radio, roles: ["superadmin", "admin"] },
  { to: "/admin", label: "Administración", icon: Cog, roles: ["superadmin"] },
  { to: "/tenants", label: "Tenants", icon: Building2, roles: ["superadmin"] },
];

// Tenants curados (demos/seeds): NUNCA se les aplica el candado de upsell.
const DEMO_IDS = ["demo-tenant-0001", "obrador-demo-0001", "brc-demo-0001", "demo-video-0001"];
// Plan que desbloquea cada nivel (para el copy del candado).
const PLAN_POR_NIVEL = { 2: "REGISTRA · 249€", 3: "TRAZA · 399€" };
const PRECIO_POR_NIVEL = { 2: "249€", 3: "399€" };

export default function Layout({ children }) {
  const { user, tenant, logout, scopeTenant } = useAuth();
  const navigate = useNavigate();
  const scopeNombre = localStorage.getItem("ar_scope_nombre");
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => n.roles.includes(user.rol));

  // Candado de upsell (solo frontend): oculta el ruido de módulos de planes superiores
  // en la prueba sin tocar niveles_activos ni el scoring. Nunca afecta a superadmin ni a demos.
  const esDemo = DEMO_IDS.includes(tenant?.id);
  const esTrial = !esDemo && (tenant?.plan === "trial" || tenant?.estado === "trial");
  // En la prueba se muestra hasta REGISTRA; solo TRAZA (nivel 3) queda con candado.
  const nivelesDisponibles = esTrial
    ? [1, 2, 4].filter((n) => (tenant?.niveles_activos || [1, 2, 3, 4]).includes(n))
    : (tenant?.niveles_activos || [1, 2, 3, 4]);
  const bloqueado = (n) =>
    user.rol !== "superadmin" && !esDemo && n.nivel != null && !nivelesDisponibles.includes(n.nivel);

  // Puerta inicial: usuario de tenant que aún no ha elegido su norma.
  if (user.rol !== "superadmin" && tenant && tenant.norma_configurada === false) {
    return <NormaGate />;
  }

  // Bloque 4.1: diagnóstico rápido estimado — tras NormaGate, antes del panel,
  // para trials de las normas con banco (SANIDAD_ES, IFS_V8, BRC_V9) que aún no lo hayan hecho/saltado.
  if (
    user.rol !== "superadmin" && tenant &&
    tenant.norma_configurada !== false &&
    ["SANIDAD_ES", "IFS_V8", "BRC_V9"].includes(tenant.norma_activa) &&
    (tenant.plan === "trial" || tenant.estado === "trial") &&
    !tenant.diagnostico_estimado_hecho
  ) {
    return <DiagnosticoRapido />;
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {open && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setOpen(false)} data-testid="sidebar-backdrop" />}

      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[#0E2A47] text-white flex flex-col transition-transform duration-200 lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}
        data-testid="sidebar"
      >
        <div className="px-6 py-6 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-[#FF5625]" size={26} />
            <div>
              <div className="font-black text-lg tracking-tighter leading-none">AuditReady</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-1">{tenant?.norma_config?.nombre_corto || "Camino IFS"}</div>
            </div>
          </div>
          <button onClick={() => setOpen(false)} className="lg:hidden text-white/70 hover:text-white" data-testid="sidebar-close"><X size={22} /></button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {items.map((n) => {
            if (bloqueado(n)) {
              const tid = n.label.toLowerCase().replace(/\s+/g, "-");
              return (
                <button
                  key={n.to}
                  onClick={() => { setOpen(false); navigate("/activar"); }}
                  data-testid={`nav-locked-${tid}`}
                  title={`Disponible en ${PLAN_POR_NIVEL[n.nivel] || "un plan superior"}`}
                  className="w-full flex items-center gap-3 px-6 py-3 text-sm font-medium text-white/35 hover:text-white/70 hover:bg-white/5 transition-colors border-l-2 border-transparent"
                >
                  <n.icon size={18} />
                  <span className="flex-1 text-left truncate">{n.label}</span>
                  <span className="shrink-0 flex items-center gap-1 text-[9px] font-bold text-[#FF5625]" data-testid={`nav-lock-badge-${tid}`}>
                    <Lock size={11} />{PRECIO_POR_NIVEL[n.nivel]}
                  </span>
                </button>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-6 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-white/10 text-white border-l-2 border-[#FF5625]"
                      : "text-white/70 hover:bg-white/5 hover:text-white border-l-2 border-transparent"
                  }`
                }
              >
                <n.icon size={18} />
                {n.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-6 py-4 border-t border-white/10">
          <div className="text-sm font-semibold truncate">{user.nombre}</div>
          <div className="text-xs text-white/50 capitalize mb-3">{user.rol}</div>
          <button
            onClick={() => { logout(); navigate("/login"); }}
            data-testid="logout-btn"
            className="flex items-center gap-2 text-xs text-white/70 hover:text-[#FF5625] transition-colors"
          >
            <LogOut size={14} /> Cerrar sesión
          </button>
        </div>
      </aside>

      <div className="lg:ml-64">
        <header className="h-14 bg-white border-b border-[#E2E8F0] flex items-center gap-3 sm:gap-6 px-4 lg:px-8 sticky top-0 z-20">
          <button onClick={() => setOpen(true)} className="lg:hidden text-[#0E2A47] shrink-0" data-testid="sidebar-open" aria-label="Abrir menú"><Menu size={24} /></button>
          {user.rol !== "superadmin" || scopeTenant ? <GlobalSearch /> : <div />}
          <div className="text-xs sm:text-sm text-[#0E2A47]/60 ml-auto truncate max-w-[45%] sm:max-w-none text-right">
            {user.rol === "superadmin" ? (
              scopeTenant ? (
                <span>Operando sobre: <b className="text-[#0E2A47]">{scopeNombre || scopeTenant}</b></span>
              ) : (
                <span className="text-[#FF5625] font-medium">Seleccione un tenant</span>
              )
            ) : (
              <span className="truncate"><b className="text-[#0E2A47]">{tenant?.nombre}</b><span className="hidden sm:inline"> · {tenant?.norma_config?.nombre_corto || tenant?.norma_config?.nombre || tenant?.norma} · Niveles {(tenant?.niveles_activos || []).join(", ")}</span></span>
            )}
          </div>
        </header>
        <main className="p-4 lg:p-8 max-w-[1600px] mx-auto"><TrialBanner />{children}</main>
      </div>
      <AriWidget />
    </div>
  );
}
